import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ShoppingCart,
  ArrowLeft,
  Minus,
  Plus,
  Truck,
  Package
} from 'lucide-react';

import axios from 'axios';
import { motion } from 'framer-motion';

import {
  addToCart,
  imgUrl
} from '../data/mockData';

import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import SEO from '../components/SEO';


const API = process.env.REACT_APP_BACKEND_URL;


export default function ProductDetail() {

  const { id } = useParams();
  const navigate = useNavigate();


  const [product, setProduct] =
    useState(null);

  const [allProducts, setAllProducts] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [selectedColor, setSelectedColor] =
    useState(null);

  const [selectedSize, setSelectedSize] =
    useState(null);

  const [selectedImage, setSelectedImage] =
    useState(null);

  const [quantity, setQuantity] =
    useState(1);


  const seoPrice =
    product
      ? Number(
          product.salePrice
          &&
          Number(product.salePrice) > 0
          &&
          Number(product.salePrice) < Number(product.price)
            ? product.salePrice
            : product.price
        ) / 100
      : 0;


  const rawSeoImage =
    product
      ? imgUrl(
          product.images?.[0]?.url
          ||
          product.image
          ||
          ''
        )
      : '';


  const seoImage =
    rawSeoImage
      ? (
          rawSeoImage.startsWith('http')
            ? rawSeoImage
            : `https://lor95.com${rawSeoImage.startsWith('/') ? '' : '/'}${rawSeoImage}`
        )
      : 'https://lor95.com/lor95.png';


  const seoDescription =
    product
      ? (
          product.description
          ||
          `${product.name} - disponueshëm te Lor95 Boutique në Berat.`
        )
      : 'Produkt nga Lor95 Boutique në Berat.';


  const seoTotalStock =
    product
      ? (
          Array.isArray(product.variants)
            ? product.variants.reduce(
                (sum, variant) =>
                  sum + Number(variant.stock || 0),
                0
              )
            : Number(product.stock || 0)
        )
      : 0;


  const productSchema =
    product
      ? {
          "@context": "https://schema.org",
          "@type": "Product",
          "name": product.name,
          "description": seoDescription,
          "image": [
            seoImage
          ],
          "sku": product.sku || undefined,
          "brand": {
            "@type": "Brand",
            "name": "Lor95 Boutique"
          },
          "offers": {
            "@type": "Offer",
            "url": `https://lor95.com/product/${id}`,
            "priceCurrency": "ALL",
            "price": seoPrice,
            "availability":
              seoTotalStock > 0
                ? "https://schema.org/InStock"
                : "https://schema.org/OutOfStock",
            "itemCondition":
              "https://schema.org/NewCondition",
            "shippingDetails": {
              "@type": "OfferShippingDetails",
              "shippingRate": {
                "@type": "MonetaryAmount",
                "value": 300,
                "currency": "ALL"
              },
              "shippingDestination": {
                "@type": "DefinedRegion",
                "addressCountry": "AL"
              },
              "deliveryTime": {
                "@type": "ShippingDeliveryTime",
                "handlingTime": {
                  "@type": "QuantitativeValue",
                  "minValue": 0,
                  "maxValue": 1,
                  "unitCode": "DAY"
                },
                "transitTime": {
                  "@type": "QuantitativeValue",
                  "minValue": 1,
                  "maxValue": 1,
                  "unitCode": "DAY"
                }
              }
            }
          }
        }
      : null;


  // ============================================================
  // LOAD PRODUCT
  // ============================================================

  useEffect(() => {

    window.scrollTo(0, 0);

    axios
      .get(
        `${API}/api/products/${id}`
      )
      .then(res => {

        const p = res.data;

        setProduct(p);


        // --------------------------------------------------------
        // AUTO SELECT FIRST COLOR
        // --------------------------------------------------------

        if (
          Array.isArray(p.variants)
          &&
          p.variants.length > 0
        ) {

          const firstColor =
            p.variants.find(
              variant =>
                variant.color
            )?.color
            || '';

          setSelectedColor(
            firstColor
          );

        }


        // --------------------------------------------------------
        // AUTO SELECT FIRST IMAGE
        // --------------------------------------------------------

        const firstImage =
          p.images?.[0]?.url
          ||
          p.image
          ||
          null;

        setSelectedImage(
          firstImage
        );

      })
      .catch(() => {

        toast.error(
          'Produkti nuk u gjet'
        );

      })
      .finally(() => {

        setLoading(false);

      });

  }, [id]);


  // ============================================================
  // HAS VARIANTS
  // ============================================================

  const hasVariants =
    useMemo(() => {

      return (
        Array.isArray(
          product?.variants
        )
        &&
        product.variants.length > 0
      );

    }, [product]);


  // ============================================================
  // COLORS
  // ============================================================

  const colors =
    useMemo(() => {

      if (!hasVariants) {
        return [];
      }

      return [
        ...new Set(
          product.variants
            .map(
              variant =>
                variant.color
            )
            .filter(Boolean)
        )
      ];

    }, [
      product,
      hasVariants
    ]);


  // ============================================================
  // IMAGES FOR SELECTED COLOR
  // ============================================================

  const visibleImages =
    useMemo(() => {

      if (!product) {
        return [];
      }

      const allImages =
        (product.images || [])
          .map(
            image =>
              image.url
          )
          .filter(Boolean);

      if (
        allImages.length > 0
      ) {
        return allImages;
      }

      if (product.image) {
        return [
          product.image
        ];
      }

      return [];

    }, [
      product
    ]);


  // ============================================================
  // UPDATE SELECTED IMAGE WHEN COLOR CHANGES
  // ============================================================

  useEffect(() => {

    if (!product) {
      return;
    }

    const images =
      Array.isArray(product.images)
        ? product.images
        : [];

    const normalizeColor = value =>
      String(value || '')
        .trim()
        .toLocaleLowerCase('sq-AL');

    const selectedColorImage =
      selectedColor
        ? images.find(
            image =>
              normalizeColor(image.color)
              ===
              normalizeColor(selectedColor)
          )
        : null;

    if (
      selectedColorImage?.url
    ) {
      setSelectedImage(
        selectedColorImage.url
      );
      return;
    }

    if (
      visibleImages.length > 0
    ) {
      setSelectedImage(
        visibleImages[0]
      );
    }

  }, [
    selectedColor,
    product,
    visibleImages
  ]);


  // ============================================================
  // VARIANTS FOR SELECTED COLOR
  // ============================================================

  const selectedColorVariants =
    useMemo(() => {

      if (
        !hasVariants
        ||
        !selectedColor
      ) {

        return [];
      }


      return product.variants.filter(
        variant =>
          (
            variant.color
            || ''
          ).toLowerCase()
          ===
          selectedColor.toLowerCase()
      );

    }, [
      product,
      selectedColor,
      hasVariants
    ]);


  // ============================================================
  // AVAILABLE SIZES
  // ============================================================

  const availableSizes =
    useMemo(() => {

      if (hasVariants) {

        return selectedColorVariants.map(
          variant => ({
            size:
              variant.size || '',

            stock:
              Number(
                variant.stock
                || 0
              )
          })
        );
      }


      return (
        product?.sizes
        || []
      ).map(
        size => ({
          size,
          stock:
            Number(
              product?.stock
              || 0
            )
        })
      );

    }, [
      product,
      hasVariants,
      selectedColorVariants
    ]);


  useEffect(() => {
    axios
      .get(`${API}/api/products`)
      .then(
        res =>
          setAllProducts(
            Array.isArray(res.data)
              ? res.data
              : []
          )
      )
      .catch(() => {
        setAllProducts([]);
      });
  }, []);


  const relatedProducts =
    useMemo(
      () => {
        if (!product) {
          return [];
        }

        const sameCategory =
          allProducts.filter(
            item =>
              item.id !== product.id
              &&
              item.category === product.category
              &&
              (
                !product.gender
                ||
                !item.gender
                ||
                item.gender === product.gender
                ||
                item.gender === 'Të gjitha'
              )
          );

        const fallback =
          allProducts.filter(
            item =>
              item.id !== product.id
              &&
              !sameCategory.some(
                related =>
                  related.id === item.id
              )
              &&
              (
                item.gender === product.gender
                ||
                item.category === product.category
              )
          );

        return [
          ...sameCategory,
          ...fallback
        ].slice(0, 4);
      },
      [
        allProducts,
        product
      ]
    );


  // ============================================================
  // SELECTED VARIANT
  // ============================================================

  const selectedVariant =
    useMemo(() => {

      if (
        !hasVariants
        ||
        !selectedColor
      ) {

        return null;
      }


      return product.variants.find(
        variant => {

          const colorMatch =
            (
              variant.color
              || ''
            ).toLowerCase()
            ===
            selectedColor.toLowerCase();


          const sizeMatch =
            (
              variant.size
              || ''
            ).toLowerCase()
            ===
            (
              selectedSize
              || ''
            ).toLowerCase();


          return (
            colorMatch
            &&
            sizeMatch
          );
        }
      ) || null;

    }, [
      product,
      hasVariants,
      selectedColor,
      selectedSize
    ]);


  // ============================================================
  // CURRENT STOCK
  // ============================================================

  const currentStock =
    useMemo(() => {

      if (!product) {
        return 0;
      }


      if (hasVariants) {

        if (selectedVariant) {

          return Number(
            selectedVariant.stock
            || 0
          );
        }


        if (selectedColor) {

          return selectedColorVariants.reduce(
            (
              sum,
              variant
            ) =>
              sum
              +
              Number(
                variant.stock
                || 0
              ),
            0
          );
        }
      }


      return Number(
        product.stock
        || 0
      );

    }, [
      product,
      hasVariants,
      selectedVariant,
      selectedColor,
      selectedColorVariants
    ]);


  // ============================================================
  // TOTAL STOCK
  // ============================================================

  const totalStock =
    useMemo(() => {

      if (!product) {
        return 0;
      }


      if (hasVariants) {

        return product.variants.reduce(
          (
            sum,
            variant
          ) =>
            sum
            +
            Number(
              variant.stock
              || 0
            ),
          0
        );
      }


      return Number(
        product.stock
        || 0
      );

    }, [
      product,
      hasVariants
    ]);


  const outOfStock =
    totalStock <= 0;


  // ============================================================
  // COLOR CHANGE
  // ============================================================

  const handleColorChange =
    color => {

      setSelectedColor(
        color
      );

      setSelectedSize(
        null
      );

      setQuantity(
        1
      );
    };


  // ============================================================
  // SIZE CHANGE
  // ============================================================

  const handleSizeChange =
    sizeItem => {

      if (
        sizeItem.stock <= 0
      ) {
        return;
      }


      setSelectedSize(
        sizeItem.size
      );

      setQuantity(
        1
      );
    };


  // ============================================================
  // ADD TO CART
  // ============================================================

  const handleAddToCart = () => {

    if (outOfStock) {

      toast.error(
        'Produkti nuk është në stok',
        { duration: 3000 }
      );

      return;
    }


    // ----------------------------------------------------------
    // NEW VARIANT PRODUCT
    // ----------------------------------------------------------

    if (hasVariants) {

      if (!selectedColor) {

        toast.error(
          'Ju lutemi zgjidhni një ngjyrë',
          { duration: 3000 }
        );

        return;
      }


      if (
        availableSizes.length > 0
        &&
        !selectedSize
      ) {

        toast.error(
          'Ju lutemi zgjidhni një masë',
          { duration: 3000 }
        );

        return;
      }


      if (
        !selectedVariant
      ) {

        toast.error(
          'Varianti i zgjedhur nuk ekziston',
          { duration: 3000 }
        );

        return;
      }


      if (
        selectedVariant.stock <= 0
      ) {

        toast.error(
          'Ky variant nuk është më në stok',
          { duration: 3000 }
        );

        return;
      }


      if (
        quantity
        >
        selectedVariant.stock
      ) {

        toast.error(
          `Në stok ka vetëm ${selectedVariant.stock} copë`,
          { duration: 3000 }
        );

        return;
      }


      const productForCart = {
        ...product,

        image:
          selectedImage
          ||
          product.image,

        selectedColor,
        selectedSize
      };


      addToCart(
        productForCart,
        quantity,
        selectedSize,
        selectedColor
      );


      window.dispatchEvent(
        new Event(
          'cartUpdated'
        )
      );


      toast.success(
        `${product.name} • ${selectedColor}${selectedSize ? ` • ${selectedSize}` : ''} u shtua në shportë!`,
        { duration: 3000 }
      );


      return;
    }


    // ----------------------------------------------------------
    // LEGACY PRODUCT
    // ----------------------------------------------------------

    if (
      product.sizes?.length > 0
      &&
      !selectedSize
    ) {

      toast.error(
        'Ju lutemi zgjidhni një masë'
      );

      return;
    }


    if (
      quantity
      >
      product.stock
    ) {

      toast.error(
        `Në stok ka vetëm ${product.stock} copë`,
        { duration: 3000 }
      );

      return;
    }


    addToCart(
      product,
      quantity,
      selectedSize
    );


    window.dispatchEvent(
      new Event(
        'cartUpdated'
      )
    );


    toast.success(
      `${product.name}${selectedSize ? ` (${selectedSize})` : ''} u shtua në shportë!`,
      { duration: 3000 }
    );
  };


  // ============================================================
  // LOADING
  // ============================================================

  if (loading) {

    return (

      <div className="min-h-screen bg-white flex items-center justify-center">

        <p className="text-gray-600">
          Duke ngarkuar produktin...
        </p>

      </div>
    );
  }


  // ============================================================
  // NOT FOUND
  // ============================================================

  if (!product) {

    return (

      <div className="min-h-screen bg-white flex items-center justify-center">

        <div className="text-center">

          <p className="text-xl text-gray-600 mb-6">
            Produkti nuk u gjet.
          </p>


          <Button
            asChild
            className="bg-gray-900 hover:bg-gray-800 text-white rounded-none"
          >

            <Link to="/collections">
              Kthehu te Koleksionet
            </Link>

          </Button>

        </div>

      </div>
    );
  }


  // ============================================================
  // RENDER
  // ============================================================

  return (

    <div className="min-h-screen bg-white pt-24 pb-20">

      <SEO
        title={product.name}
        description={seoDescription}
        path={`/product/${id}`}
        image={seoImage}
        type="product"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* BACK */}

        <button
          data-testid="product-back-btn"

          onClick={
            () =>
              navigate(-1)
          }

          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-8 transition-colors"
        >

          <ArrowLeft className="h-4 w-4 mr-2" />

          Kthehu

        </button>


        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">


          {/* =====================================================
              IMAGE GALLERY
          ====================================================== */}

          <motion.div
            initial={{
              opacity: 0,
              x: -30
            }}

            animate={{
              opacity: 1,
              x: 0
            }}

            transition={{
              duration: 0.7,
              ease: [
                0.22,
                1,
                0.36,
                1
              ]
            }}

            className="space-y-4"
          >

            {/* MAIN IMAGE */}

            <div className="relative overflow-hidden aspect-[3/4] bg-gray-50">

              {selectedImage ? (

                <img
                  src={
                    imgUrl(
                      selectedImage
                    )
                  }

                  alt={
                    product.name
                  }

                  className="w-full h-full object-cover"
                />

              ) : (

                <div className="w-full h-full flex items-center justify-center text-gray-400">
                  Nuk ka foto
                </div>

              )}


              <div className="absolute top-5 left-5 flex flex-col gap-2">

                {product.newArrival && (

                  <span className="bg-white text-gray-900 text-xs font-bold px-3 py-1.5 shadow-sm">

                    E RE

                  </span>

                )}


                {product.bestSeller && (

                  <span className="bg-gray-900 text-white text-xs font-bold px-3 py-1.5">

                    MË E SHITUR

                  </span>

                )}


                {outOfStock && (

                  <span className="bg-red-600 text-white text-xs font-bold px-3 py-1.5">

                    E SHITUR

                  </span>

                )}

              </div>

            </div>


            {/* THUMBNAILS */}

            {visibleImages.length > 1 && (

              <div className="grid grid-cols-4 sm:grid-cols-5 gap-3">

                {visibleImages.map(
                  (
                    image,
                    index
                  ) => (

                    <button
                      key={`${image}-${index}`}

                      type="button"

                      onClick={
                        () =>
                          setSelectedImage(
                            image
                          )
                      }

                      className={`
                        relative
                        aspect-square
                        overflow-hidden
                        border-2
                        transition-all

                        ${
                          selectedImage
                          === image

                            ? 'border-gray-900'

                            : 'border-gray-200 hover:border-gray-500'
                        }
                      `}
                    >

                      <img
                        src={
                          imgUrl(
                            image
                          )
                        }

                        alt={
                          `${product.name} ${index + 1}`
                        }

                        className="w-full h-full object-cover"
                      />

                    </button>

                  )
                )}

              </div>

            )}

          </motion.div>


          {/* =====================================================
              PRODUCT INFO
          ====================================================== */}

          <motion.div
            initial={{
              opacity: 0,
              x: 30
            }}

            animate={{
              opacity: 1,
              x: 0
            }}

            transition={{
              duration: 0.7,
              delay: 0.1,
              ease: [
                0.22,
                1,
                0.36,
                1
              ]
            }}

            className="space-y-7"
          >

            <div>

              <p className="text-sm font-medium text-gray-500 uppercase tracking-widest mb-3">

                {product.gender}
                {' • '}
                {product.season}
                {' • '}
                {product.category}

              </p>


              <h1
                data-testid="product-detail-name"

                className="text-4xl sm:text-5xl font-serif font-bold text-gray-900 leading-tight"
              >

                {product.name}

              </h1>

            </div>


            {/* PRICE */}

            <div data-testid="product-detail-price">
              {(
                product.salePrice != null
                &&
                Number(product.salePrice) > 0
                &&
                Number(product.salePrice) < Number(product.price)
              ) ? (
                <div className="flex items-baseline gap-3">
                  <span className="text-xl text-gray-400 line-through">
                    {(product.price / 100).toFixed(0)} Lek
                  </span>

                  <span className="text-3xl font-bold text-red-600">
                    {(product.salePrice / 100).toFixed(0)} Lek
                  </span>

                  <span className="text-xs font-bold bg-red-600 text-white px-2 py-1">
                    -{Math.round(
                      (
                        (
                          Number(product.price)
                          -
                          Number(product.salePrice)
                        )
                        /
                        Number(product.price)
                      )
                      *
                      100
                    )}%
                  </span>
                </div>
              ) : (
                <p className="text-3xl font-bold text-gray-900">
                  {(product.price / 100).toFixed(0)} Lek
                </p>
              )}
            </div>


            {/* DESCRIPTION */}

            <p className="text-lg text-gray-600 leading-relaxed">

              {product.description}

            </p>


            {/* =================================================
                TOTAL STOCK
            ================================================== */}

            <div className="flex items-center gap-2 text-sm">

              <Package className="h-4 w-4 text-gray-500" />


              {outOfStock ? (

                <span
                  data-testid="product-stock-status"

                  className="text-red-600 font-semibold"
                >

                  E shitur — jo në stok

                </span>

              ) : (

                <span
                  data-testid="product-stock-status"

                  className="text-gray-700"
                >

                  Në stok gjithsej:
                  {' '}

                  <strong>
                    {totalStock}
                  </strong>
                  {' '}
                  copë

                </span>

              )}

            </div>


            {/* =================================================
                COLORS
            ================================================== */}

            {hasVariants && colors.length > 0 && (

              <div>

                <h3 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wide">

                  Zgjidhni Ngjyrën

                </h3>


                <div className="flex flex-wrap gap-2">

                  {colors.map(
                    color => {

                      const colorStock =
                        product.variants
                          .filter(
                            variant =>
                              (
                                variant.color
                                || ''
                              ).toLowerCase()
                              ===
                              color.toLowerCase()
                          )
                          .reduce(
                            (
                              sum,
                              variant
                            ) =>
                              sum
                              +
                              Number(
                                variant.stock
                                || 0
                              ),
                            0
                          );


                      const soldOut =
                        colorStock <= 0;


                      return (

                        <button
                          key={color}

                          type="button"

                          disabled={soldOut}

                          onClick={
                            () =>
                              handleColorChange(
                                color
                              )
                          }

                          className={`
                            px-5
                            py-2.5
                            border
                            text-sm
                            font-medium
                            transition-all

                            ${
                              soldOut

                                ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed line-through'

                                : selectedColor === color

                                  ? 'bg-gray-900 text-white border-gray-900'

                                  : 'bg-white text-gray-700 border-gray-300 hover:border-gray-900'
                            }
                          `}
                        >

                          {color}

                          <span className="ml-2 text-xs opacity-70">

                            ({colorStock})

                          </span>

                        </button>

                      );
                    }
                  )}

                </div>

              </div>

            )}


            {/* =================================================
                SIZES
            ================================================== */}

            {availableSizes.length > 0 && (

              <div>

                <h3 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wide">

                  Zgjidhni Masën

                  {
                    product.category
                    === 'Këpucë'

                      ? ' (Numri i Këmbës)'

                      : ''
                  }

                </h3>


                <div className="flex flex-wrap gap-2">

                  {availableSizes.map(
                    sizeItem => {

                      const soldOut =
                        sizeItem.stock <= 0;


                      return (

                        <button
                          key={
                            sizeItem.size
                            || 'no-size'
                          }

                          data-testid={`size-option-${sizeItem.size}`}

                          disabled={soldOut}

                          onClick={
                            () =>
                              handleSizeChange(
                                sizeItem
                              )
                          }

                          className={`
                            min-w-[60px]
                            px-4
                            py-2.5
                            border
                            text-sm
                            font-medium
                            transition-all
                            duration-200

                            ${
                              soldOut

                                ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed line-through'

                                : selectedSize === sizeItem.size

                                  ? 'bg-gray-900 text-white border-gray-900'

                                  : 'bg-white text-gray-700 border-gray-300 hover:border-gray-900'
                            }
                          `}
                        >

                          {
                            sizeItem.size
                            ||
                            'Pa masë'
                          }

                          <span className="block text-[10px] mt-1 opacity-70">

                            {
                              soldOut
                                ? 'Pa stok'
                                : `${sizeItem.stock} copë`
                            }

                          </span>

                        </button>

                      );
                    }
                  )}

                </div>

              </div>

            )}


            {/* =================================================
                SELECTED VARIANT STOCK
            ================================================== */}

            {hasVariants && selectedColor && selectedSize && (

              <div className="bg-gray-50 border border-gray-200 px-4 py-3">

                <p className="text-sm text-gray-700">

                  Varianti i zgjedhur:
                  {' '}

                  <strong>
                    {selectedColor}
                  </strong>

                  {' / '}

                  <strong>
                    {selectedSize}
                  </strong>

                </p>


                <p className="text-sm mt-1">

                  Në stok:
                  {' '}

                  <strong
                    className={
                      currentStock <= 0
                        ? 'text-red-600'
                        : 'text-green-700'
                    }
                  >

                    {currentStock}

                  </strong>
                  {' '}
                  copë

                </p>

              </div>

            )}


            {/* =================================================
                QUANTITY
            ================================================== */}

            <div>

              <h3 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wide">

                Sasia

              </h3>


              <div className="flex items-center space-x-3">

                <button
                  data-testid="quantity-decrease"

                  onClick={
                    () =>
                      setQuantity(
                        q =>
                          Math.max(
                            1,
                            q - 1
                          )
                      )
                  }

                  disabled={
                    quantity <= 1
                  }

                  className="w-10 h-10 flex items-center justify-center border border-gray-300 hover:border-gray-900 disabled:opacity-50 transition-colors"
                >

                  <Minus className="h-4 w-4" />

                </button>


                <span
                  data-testid="quantity-value"

                  className="text-lg font-semibold w-10 text-center"
                >

                  {quantity}

                </span>


                <button
                  data-testid="quantity-increase"

                  onClick={
                    () => {

                      const maxStock =
                        hasVariants
                          ? (
                              selectedVariant
                                ? selectedVariant.stock
                                : 1
                            )
                          : product.stock;


                      setQuantity(
                        q =>
                          Math.min(
                            maxStock || 1,
                            q + 1
                          )
                      );
                    }
                  }

                  disabled={
                    hasVariants
                      ? (
                          !selectedVariant
                          ||
                          quantity
                          >= selectedVariant.stock
                        )
                      : (
                          quantity
                          >= product.stock
                        )
                  }

                  className="w-10 h-10 flex items-center justify-center border border-gray-300 hover:border-gray-900 disabled:opacity-50 transition-colors"
                >

                  <Plus className="h-4 w-4" />

                </button>

              </div>

            </div>


            {/* =================================================
                ADD TO CART
            ================================================== */}

            <Button
              data-testid="detail-add-to-cart-btn"

              onClick={
                handleAddToCart
              }

              disabled={
                outOfStock
              }

              size="lg"

              className="w-full sm:w-auto bg-gray-900 hover:bg-gray-800 text-white font-semibold rounded-none px-12 py-7 text-base transition-all duration-300 hover:scale-[1.02] disabled:opacity-50"
            >

              <ShoppingCart className="mr-2 h-5 w-5" />


              {
                outOfStock
                  ? 'E Shitur'
                  : 'Shto në Shportë'
              }

            </Button>


            {/* SHIPPING */}

            <div className="flex items-center gap-3 text-sm text-gray-500 pt-4 border-t border-gray-100">

              <Truck className="h-5 w-5" />

              <span>

                Pagesa në dorëzim
                {' • '}
                Transporti kushton 300 Lek për çdo porosi

              </span>

            </div>

          </motion.div>

        </div>

      </div>


      {/* =====================================================
          PRODUKTE TË NGJASHME
      ====================================================== */}

      {relatedProducts.length > 0 && (

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16 lg:mt-24">

          <div className="flex items-end justify-between gap-4 mb-7">

            <div>

              <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[#b78332]">
                Mund t'ju pëlqejnë
              </p>

              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-gray-900 mt-2">
                Produkte të Ngjashme
              </h2>

            </div>

            <Link
              to={`/collections?category=${encodeURIComponent(product.category)}`}
              className="text-xs sm:text-sm font-semibold tracking-wide text-gray-700 hover:text-[#b78332] transition-colors"
            >
              SHIKO TË GJITHA →
            </Link>

          </div>


          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">

            {relatedProducts.map(item => {

              const hasSale =
                item.salePrice != null
                &&
                Number(item.salePrice) > 0
                &&
                Number(item.salePrice) < Number(item.price);

              const discountPercent =
                hasSale
                  ? Math.round(
                      (
                        (
                          Number(item.price)
                          -
                          Number(item.salePrice)
                        )
                        /
                        Number(item.price)
                      )
                      *
                      100
                    )
                  : 0;

              return (

                <Link
                  key={item.id}
                  to={`/product/${item.id}`}
                  className="group"
                >

                  <div className="relative aspect-[3/4] bg-gray-100 overflow-hidden">

                    <img
                      src={imgUrl(
                        item.image
                        ||
                        item.images?.[0]?.url
                      )}
                      alt={item.name}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />

                    {hasSale && (

                      <span className="absolute top-3 left-3 bg-red-600 text-white text-[10px] sm:text-xs font-bold px-2 py-1">
                        -{discountPercent}%
                      </span>

                    )}

                  </div>


                  <div className="pt-3">

                    <p className="text-xs text-gray-500">
                      {item.category}
                    </p>

                    <h3 className="font-medium text-gray-900 mt-1 line-clamp-1 group-hover:text-[#b78332] transition-colors">
                      {item.name}
                    </h3>

                    {hasSale ? (

                      <div className="flex flex-wrap items-baseline gap-2 mt-1">

                        <span className="text-xs text-gray-400 line-through">
                          {(item.price / 100).toFixed(0)} Lek
                        </span>

                        <span className="font-bold text-red-600">
                          {(item.salePrice / 100).toFixed(0)} Lek
                        </span>

                      </div>

                    ) : (

                      <p className="font-bold text-gray-900 mt-1">
                        {(item.price / 100).toFixed(0)} Lek
                      </p>

                    )}

                  </div>

                </Link>
              );
            })}

          </div>

        </section>

      )}


    </div>
  );
}